<template>
  <layout name="Sidebar">
    <WithLoader :isLoading="!campaign || !tags.length" classType="primary">
      <div v-if="campaign" class="campaign-details__wrapper">
        <v-sidebar-layout-heading
          title="Campaign Details"
          :buttons="buttons"
          extraClass="campaign-details__heading"
        >
          <span class="mail-status" :class="{ [getMailStatus]: true }">{{
            getMailStatus
          }}</span>
        </v-sidebar-layout-heading>

        <div
          class="campaign-details__cant-send"
          v-if="!campaign.mailed && template.deleted_at"
        >
          This campaign cannot be send due to deleted template.
        </div>

        <div class="campaign-details__content">
          <CampaignData
            :name="campaign.name"
            :created="formatDate(campaign.created)"
            :mailed="formatDate(campaign.mailed)"
            :updated="formatDate(campaign.updated)"
            :accepted="formatDate(campaign.accepted)"
            :template="{
              id: template.id,
              name: template.name,
              deleted_at: formatDate(template.deleted_at)
            }"
            :complexType="getComplexType"
            :campaignType="campaign.type"
            :tags="tagsLabels"
          ></CampaignData>
          <div class="thumbnails">
            <VThumbnailsPreview
              :thumbnails="getThumbnails"
              :type="campaign.type"
              :maxItemsCount="maxThumbnailsCount"
            ></VThumbnailsPreview>
          </div>
        </div>

        <div class="campaign-details__tracking-events">
          <span class="label">Tracking Events</span>
          <div v-if="!getMailStatistics" class="empty">No Tracking Events</div>
          <VPieChart
            v-else
            :mail_statistics="convertDataToPieChart"
          ></VPieChart>
        </div>

        <CampaignsCreateNewModal
          :campaignId="id"
          :opened="editCampaignOpened"
          @close="editCampaignOpened = false"
          @campaigncreated="editCampaignOpened = false"
        ></CampaignsCreateNewModal>

        <CampaignsPreviewModal
          :campaignId="id"
          :opened="previewCampaignOpened"
          :templateId="template.id"
          :thumbnails="getThumbnails"
          @close="previewCampaignOpened = false"
        >
        </CampaignsPreviewModal>

        <!--<div v-if="renderingPreview" class="rendering-overflow">-->
        <!--<div class="inner-rendering">-->
        <!--Rendering template, please wait... it will be opened automatically-->
        <!--in a new window.-->
        <!--</div>-->
        <!--</div>-->
      </div>
    </WithLoader>
  </layout>
</template>

<script>
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import Layout from "@/layouts/Layout";
import moment from "moment";

// components
import VSidebarLayoutHeading from "@/components/VSidebarLayoutHeading";
import CampaignsCreateNewModal from "@/components/CampaignsCreateNewModal";
import CampaignsPreviewModal from "@/components/CampaignsPreviewModal";
import VPreviewRenderedTemplate from "@/components/VPreviewRenderedTemplate";
import WithLoader from "@/components/WithLoader";
import VThumbnailsPreview from "@/components/VThumbnailsPreview";
import CampaignData from "@/components/CampaignData";
import VPieChart from "@/components/VPieChart";

import previewImageVertical from "@/assets/images/letters-preview-big.svg";
import betaIconSrc from "@/assets/images/beta.svg";

// mixins
import withFormUtils from "@/mixins/withFormUtils";
import withToasted from "@/mixins/withToasted";
import withNavigate from "@/mixins/withNavigate";

// utils
import {
  getPostcardFullType,
  getLetterType,
  convertTagsToSelectListFormat
} from "@/utils/util";

//api
import { fetchContactsList } from "@/api-client/contacts";
import { fetchTagsList } from "@/api-client/tag";

export default Vue.component("CampaignDetails", {
  metaInfo: {
    title: "Campaign Details"
  },

  components: {
    Layout,
    VSidebarLayoutHeading,
    CampaignsPreviewModal,
    WithLoader,
    CampaignData,
    CampaignsCreateNewModal,
    VThumbnailsPreview,
    VPieChart
  },

  mixins: [withNavigate, withFormUtils, withToasted],

  data() {
    return {
      betaIconSrc,
      id: null,
      editCampaignOpened: false,
      previewCampaignOpened: false,
      maxThumbnailsCount: 2,
      renderingPreview: false,
      tags: [],
      mail_statistics: [
        {
          key: "processed",
          label: "Processed",
          color: "#979797"
        },
        {
          key: "mailed_at",
          label: "Mailed",
          color: "#5757F5"
        },
        {
          key: "in_transit_at",
          label: "In Transit",
          color: "#FFA34D"
        },
        {
          key: "in_local_area_at",
          label: "In Local Area",
          color: "#FFCB4C"
        },

        {
          key: "processed_for_delivery_at",
          label: "Processed For Delivery",
          color: "#29CC94"
        },

        {
          key: "re_routed_at",
          label: "Re-Routed",
          color: "#FFA196"
        },

        {
          key: "returned_to_sender_at",
          label: "Returned To Sender",
          color: "#EE4545"
        }
      ]
    };
  },

  methods: {
    async onButtonClick(e, key) {
      switch (key) {
        case "edit-campaign":
          this.editCampaignOpened = true;
          break;

        case "preview-campaign":
          this.previewCampaignOpened = true;
          break;

        case "preview-template":
          this.renderingPreview = true;

          // retrieve contacts list by tag ID
          // to proper render merge params
          const filteredByTagsContacts = await fetchContactsList({
            queryString: `&data-tags=${this.tagsId[0]}`
          });

          if (
            filteredByTagsContacts &&
            filteredByTagsContacts.items &&
            !filteredByTagsContacts.items.length
          ) {
            this.toast(
              "No contacts found for given tags. Preview cannot be rendered. Try to use different tags.",
              "error"
            );

            this.renderingPreview = false;
            return;
          }

          const [firstFilteredContact] = filteredByTagsContacts.items;

          const {
            id: contactId,
            first_name,
            last_name,
            address_city,
            address_country,
            address_zip,
            address_line_1,
            address_line_2,
            company
          } = firstFilteredContact;

          const returnAddress = this.campaign.fromAddress;

          this.triggerTestPreview({
            data: {
              template_id: this.template.id,
              contact_id: contactId,
              product_type:
                this.campaign.type === "letter" && this.template.product_code_id
                  ? "custom"
                  : this.campaign.type === "letter"
                  ? "#10"
                  : this.campaign.type,
              return_address: returnAddress,
              merge_params: {
                first_name,
                last_name,
                address_city,
                address_country,
                address_zip,
                address_line_1,
                address_line_2,
                company,
                ...firstFilteredContact.custom_data
              }
            },

            onSuccess: pdf_url => {
              this.renderingPreview = false;

              if (pdf_url) {
                window.open(pdf_url, "_blank");
              }
            },

            onError: () => {
              this.renderingPreview = false;

              this.toast(
                "Rendering preview failed. Please, try again later.",
                "error"
              );
            }
          });
          break;
      }
    },

    formatDate(date) {
      return date
        ? {
            dataOnly: moment(date).format("MMM D, Y"),
            timeOnly: moment(date).format("HH:mm")
          }
        : null;
    },

    async fetchTags() {
      const tagsString = `&id=${this.tagsId.join(",")}`;
      const tags = await fetchTagsList({ queryString: tagsString });
      return tags.items;
    },

    ...mapActions("campaigns", ["fetchCampaign"]),
    ...mapActions("templates", ["triggerTestPreview"]),
    ...mapActions("tags", ["fetchTagsList"])
  },

  computed: {
    tagsLabels() {
      return this.tags.map(tag => tag.label);
    },

    buttons() {
      // no actions displayed
      if (this.isCampaignFrozen(this.id)) {
        return [];
      }

      return [
        {
          key: "campaign-edit",
          type: "primary",
          label: "Edit",
          inverted: true,
          noBorder: true,
          size: "narrow",
          onClickHandler: e => this.onButtonClick(e, "edit-campaign")
        },
        {
          key: "preview-campaign",
          component: VPreviewRenderedTemplate,
          props: {
            onClickHandler: e => this.onButtonClick(e, "preview-template"),
            disabled: this.renderingPreview || !this.tags.length
          }
        },
        {
          key: "campaign-preview",
          type: "primary",
          label: "Send",
          size: "regular",
          onClickHandler: e => this.onButtonClick(e, "preview-campaign"),
          hidden: this.campaign.accepted,
          disabled:
            this.renderingPreview ||
            !this.sentPieces ||
            !this.tags.length ||
            this.template.deleted_at
        }
      ];
    },

    campaign() {
      return this.getCampaign(this.id);
    },

    tagsId() {
      if (this.campaign) {
        return this.campaign.tags;
      }
      return [];
    },

    address() {
      return this.campaign.fromAddress;
    },

    template() {
      return this.campaign.template || {};
    },

    getMailStatus() {
      if (this.campaign) {
        const campaign = this.campaign;
        if (campaign.mailed) {
          return "finished";
        } else if (campaign.accepted) {
          return "processing";
        } else {
          return "draft";
        }
      }
    },

    sentPieces() {
      const preview = this.getPreview(this.campaign.id);
      return preview ? preview.count : null;
    },

    getComplexType() {
      if (this.campaign.type === "postcard") {
        return getPostcardFullType(this.template.type, this.template.mail_type);
      }
      return getLetterType(this.template.color, this.template.mail_type);
    },

    getThumbnails() {
      if (this.template) {
        if (this.campaign.type === "postcard") {
          return [
            this.template.front_thumbnail_url,
            this.template.back_thumbnail_url
          ];
        } else {
          let thumbnailsArr = this.template.thumbnails_urls;
          if (!thumbnailsArr) {
            thumbnailsArr = [previewImageVertical];
            return thumbnailsArr;
          }
          return thumbnailsArr;
        }
      }
    },

    getMailStatistics() {
      return this.campaign.mail_statistics;
    },

    convertDataToPieChart() {
      if (this.getMailStatistics) {
        this.mail_statistics.map(category => {
          const findCategory = this.getMailStatistics.filter(
            fetchedCategory => fetchedCategory.status === category.key
          );
          category.value = findCategory[0].count;
        });

        return this.mail_statistics;
      }
    },

    ...mapGetters("campaigns", [
      "getCampaign",
      "isCampaignFrozen",
      "getPreview"
    ]),
    ...mapGetters("campaigns", { isCampaignsLoading: "isLoading" }),
    ...mapGetters("tags", { isTagsLoading: "isLoading" }),
    ...mapGetters("contacts", ["getTag"])
  },

  created() {
    this.id = this.$route.params.id;

    if (!this.id) {
      this.navigate("/campaigns");
    }

    // isLoading flag prevents double creation of the component
    if (!this.isCampaignsLoading) {
      this.fetchCampaign({
        id: this.id,
        onError: () => {
          this.toast("Cannot load campaign", "error");
        }
      });
    }
  },

  watch: {
    tagsId() {
      this.fetchTags().then(resp => {
        this.tags = convertTagsToSelectListFormat(resp);
      });
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "campaign-details";

// rendering-preview styles
// todo: move to an dedicated component
.rendering-overflow {
  top: 0;
  left: 0;
  z-index: 9999;
  position: fixed;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);

  .inner-rendering {
    display: flex;
    justify-content: space-between;
    border: none;
    background-color: #fafafa;
    width: 70%;
    max-height: 500px;
    box-shadow: 0 4px 2px -2px #d2d2d2;
    padding: 40px;
    max-width: 900px;
  }
}

.#{$ns}__content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.thumbnails {
  max-width: 50%;
  &/deep/.v-thumbnails-preview__wrapper {
    max-height: 222px;
    .v-preview-image {
      max-width: 46%;
      .image-wrapper {
        max-width: 100%;
        height: 100%;
      }
    }
  }
}

.#{$ns}__cant-send {
  text-align: right;
  color: $danger-color;
}

.#{$ns}__tracking-events {
  .label {
    font-size: 16px;
  }

  .empty {
    text-align: center;
    margin-top: 30px;
    font-family: Lato-Bold, Arial, sans-serif;
    font-size: 26px;
    line-height: 1.3;
    color: $empty-grey;
  }
}

/*Media*/

@media screen and (max-width: 991px) {
  .campaign-details__content {
    flex-direction: column;
    width: 100%;
  }

  .campaign-data {
    margin-right: 0;
  }

  /*maybe need implement it directly to VThumbnailsPreview*/
  .thumbnails {
    margin-top: 30px;
    max-width: 100%;
    width: 100%;
    justify-content: space-around;

    /deep/.v-thumbnails-preview__wrapper {
      max-height: none;
      .v-preview-image {
        max-width: none;
        max-height: none;
        .image-wrapper {
          width: 100%;
          height: 100%;
        }
      }
    }
  }
}

@media screen and (max-width: 600px) {
  .#{$ns}__content {
    .thumbnails {
      display: none;
    }
  }
}
</style>

<style lang="scss">
.sidebar-layout-heading.campaign-details__heading {
  padding-right: 0;

  /*  /deep/ .sidebar-layout-heading__title {
      align-items: end;
    }*/
}
.mail-status {
  display: inline-block;
  margin-left: 15px;
  font-size: 10px;
  color: $tertiary-text-color;
  text-transform: capitalize;

  &.draft {
    color: $danger-color;
  }

  &.processing {
    color: $warning-color;
  }
}

.v-preview-rendered__wrapper {
  margin-right: 30px;
}
</style>
